<template>
  <div class="identity-shell">
    <ContainerBase class="identity-shell__container">
      <RowBase class="justify-content-center">
        <ColBase xs="12" md="11" lg="10" xl="9">
          <RowBase class="g-4 g-lg-5 align-items-stretch">
            <ColBase
              v-if="$slots.masthead"
              xs="12"
              lg="5"
              class="identity-shell__masthead"
            >
              <slot name="masthead" />
            </ColBase>
            <ColBase xs="12" :lg="$slots.masthead ? '7' : '12'">
              <article class="identity-shell__card">
                <div class="identity-shell__card-inner">
                  <header
                    v-if="$slots.eyebrow || $slots.title || $slots.lede"
                    class="identity-shell__header"
                  >
                    <p
                      v-if="$slots.eyebrow"
                      class="identity-shell__eyebrow"
                      :class="`identity-shell__eyebrow--${eyebrowTone}`"
                    >
                      <slot name="eyebrow" />
                    </p>
                    <h1 v-if="$slots.title" class="identity-shell__title">
                      <slot name="title" />
                    </h1>
                    <p v-if="$slots.lede" class="identity-shell__lede">
                      <slot name="lede" />
                    </p>
                  </header>
                  <slot />
                </div>
              </article>
            </ColBase>
          </RowBase>
        </ColBase>
      </RowBase>
    </ContainerBase>
  </div>
</template>

<script setup lang="ts">
import { ColBase, ContainerBase, RowBase } from '@/components/base/Grid';

const { eyebrowTone = 'primary' } = defineProps<{
  eyebrowTone?: 'primary' | 'success';
}>();
</script>

<style lang="scss" scoped>
$ink: #111827;
$muted: #6b7280;
$line: #e5e7eb;
$brand-blue: #1984c4;
$brand-blue-soft: #cfe6f3;
$brand-green: #8ac926;
$brand-green-deep: #5a7d1a;
$shadow-blue: #143659;

.identity-shell {
  min-height: 100vh;
  padding-block: 3rem 4rem;
  // Layered atmosphere lifted from getcommunal.com:
  //   1. A soft brand wash (communal-50 → atlantis-50 → white) painted with
  //      two off-screen radial gradients so the page feels brand-tinted, not
  //      antiseptic.
  //   2. The bg-paper dot texture on top for ledger-paper character.
  background-color: #f4faff;
  background-image: radial-gradient(
      circle at 20% 30%,
      rgba(15, 23, 42, 0.025) 1px,
      transparent 0
    ),
    radial-gradient(
      circle at 70% 80%,
      rgba(15, 23, 42, 0.035) 1px,
      transparent 0
    ),
    radial-gradient(
      ellipse 70% 55% at 12% 0%,
      rgba(161, 212, 239, 0.55),
      transparent 60%
    ),
    radial-gradient(
      ellipse 60% 45% at 92% 100%,
      rgba(218, 242, 176, 0.45),
      transparent 65%
    );
  background-size:
    14px 14px,
    22px 22px,
    auto,
    auto;
  background-attachment: fixed, fixed, fixed, fixed;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;

  @media (min-width: 992px) {
    padding-block: 5rem 6rem;
  }
}

.identity-shell__masthead {
  padding-inline: 0.5rem;

  @media (min-width: 992px) {
    padding-inline-end: 3rem;
  }
}

.identity-shell__card {
  position: relative;
  background: #fff;
  border: 1px solid $line;
  border-radius: 0.875rem;
  height: 100%;
  // receipt-card shadow lifted from getcommunal.com — tinted with deep navy
  box-shadow:
    0 1px rgba(0, 0, 0, 0.04),
    0 18px 38px -16px rgba(20, 54, 89, 0.28);
}

.identity-shell__card-inner {
  padding: 2rem 1.5rem;

  @media (min-width: 576px) {
    padding: 2.5rem 2.25rem;
  }

  @media (min-width: 992px) {
    padding: 2.75rem 2.5rem;
  }
}

.identity-shell__header {
  margin-bottom: 1.75rem;
}

// stamp: postal-stamp eyebrow lifted from getcommunal.com — pill-shaped with
// a dashed inner ring (the .stamp / .stamp::before pattern from the
// marketing site).
.identity-shell__eyebrow {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 1.25rem;
  padding: 0.5em 1em;
  background: #fff;
  border: 2px solid currentColor;
  border-radius: 999px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(20, 54, 89, 0.12);

  &::before {
    content: '';
    position: absolute;
    inset: 3px;
    border: 1px dashed currentColor;
    border-radius: 999px;
    opacity: 0.55;
    pointer-events: none;
  }

  &--primary {
    color: $brand-blue;
  }

  &--success {
    color: $brand-green-deep;
  }
}

.identity-shell__title {
  margin: 0 0 0.625rem;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 1.625rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.018em;
  color: $ink;
  text-wrap: balance;

  @media (min-width: 992px) {
    font-size: 1.75rem;
  }
}

.identity-shell__lede {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.55;
  color: $muted;
  text-wrap: pretty;

  strong {
    color: $ink;
    font-weight: 600;
  }
}
</style>
